import * as amplitude from "@amplitude/analytics-browser";
import { useCallback, useMemo, useState } from "react";
import { useContentConversion } from "src/components/WysiwygEditor/lexical/hooks/useContentConversion";
import { useHTMLConfig } from "src/components/WysiwygEditor/lexical/hooks/useHTMLConfig";
import AllNodes from "src/components/WysiwygEditor/lexical/nodes/AllNodes";
import { createHeadlessEditor } from "src/components/WysiwygEditor/lexical/utils/headlessEditor";
import {
  MessageContextSchema,
  TriggerEventName,
} from "src/generated/hooks/avela-ai/requests";
import { useAbortController } from "src/hooks/useAbortController";
import { useGetEnglishLanguageName } from "src/hooks/useGetEnglishLanguageName";
import { usePrompt } from "src/hooks/usePrompt";
import { translateMessages } from "src/services/messageTranslation";
import { message_template_type_enum } from "src/types/graphql";
import type { VariablesContext } from "src/types/messages/variables";
import { TranslationPreviewDrawer } from "../components/TranslationPreviewDrawer";
import {
  TranslationProgressDrawer,
  useTranslationProgress,
} from "../components/TranslationProgressDrawer";
import { MessageContent, MessageFormType } from "../schemas";

function triggerEventToVariablesContext(
  triggerEventName: TriggerEventName
): VariablesContext {
  if (triggerEventName === "Ad hoc") {
    return { type: "adhoc" };
  }
  return {
    type: "triggered",
    messageTemplateType:
      message_template_type_enum[
        triggerEventName as keyof typeof message_template_type_enum
      ],
  };
}

export function useMessageTranslation(
  translatableLanguages: string[],
  receiversInfosByLanguage: Record<string, unknown[]>,
  hasRecipientsWithEmail: boolean,
  hasRecipientsWithPhone: boolean,
  messageContext?: MessageContextSchema
) {
  const [isTranslating, setIsTranslating] = useState(false);
  const getLanguageName = useGetEnglishLanguageName();

  const getOriginalMessage = useCallback((values: MessageFormType) => {
    return values.messages.find((message) => message.language === "en");
  }, []);

  const variablesContext = useMemo(
    () =>
      messageContext
        ? triggerEventToVariablesContext(messageContext.trigger_event_name)
        : undefined,
    [messageContext]
  );

  const htmlConfig = useHTMLConfig({
    variableMode: "collapsed",
    enableVariables: true,
  });

  const converter = useContentConversion({
    variableMode: "collapsed",
    enableVariables: true,
    variablesContext,
  });

  const headlessEditor = useMemo(
    () =>
      createHeadlessEditor({
        nodes: AllNodes,
        html: htmlConfig,
        theme: {},
      }),
    [htmlConfig]
  );

  const translationProgress = useTranslationProgress();
  const {
    prompt: confirmTranslation,
    resolve: resolveTranslation,
    promptProps: translationPromptProps,
  } = usePrompt<MessageContent[], MessageContent[]>();

  const {
    createController,
    cancel: abortCancel,
    isAborted,
    clearRef,
  } = useAbortController();

  const cancelTranslation = useCallback(() => {
    abortCancel(translationProgress.cancel);
  }, [abortCancel, translationProgress.cancel]);

  const handleTranslate = useCallback(
    async (values: MessageFormType, targetLanguages?: string[]) => {
      const controller = createController();

      try {
        setIsTranslating(true);
        translationProgress.onOpen();
        translationProgress.onPreparations();

        const originalMessage = getOriginalMessage(values);
        if (!originalMessage) {
          throw new Error("Original message is undefined");
        }
        const originalEmailLexical = converter.parse(
          originalMessage.email_lexical
        );

        const { html, attachments } = converter.serialized.toHtmlWithCIDs(
          headlessEditor,
          originalEmailLexical
        );

        const originalSmsLexical = converter.parse(originalMessage.sms_lexical);
        const smsText = converter.serialized.toPlainText(
          headlessEditor,
          originalSmsLexical
        );

        translationProgress.onTranslating();

        const responses = await translateMessages(
          targetLanguages ?? translatableLanguages,
          originalMessage.subject ?? "",
          html,
          smsText ?? "",
          messageContext,
          controller.signal
        );

        if (isAborted(controller)) {
          amplitude.track("Clicked abort ad-hoc translation", {
            ...messageContext,
            has_email_content: hasRecipientsWithEmail,
            has_sms_content: hasRecipientsWithPhone,
            translatable_languages: targetLanguages ?? translatableLanguages,
          });
          return undefined;
        }

        translationProgress.onFinishing();

        // strip the english translation from the response if AI generated it
        const translations = responses
          .filter((response) => response.translated_language !== "en")
          .map((response): MessageContent => {
            if (response.status === "rejected") {
              return {
                error_message: response.reason,
                subject: "",
                email_lexical: converter.serialized.fromHtml(
                  headlessEditor,
                  "<p></p>"
                ),
                sms_lexical: converter.serialized.fromPlainText(
                  headlessEditor,
                  ""
                ),
                language: response.translated_language,
                language_name: getLanguageName(response.translated_language),
              };
            }
            const translatedEmailLexical = response.email
              ? converter.restoreCIDs(
                  converter.serialized.fromHtml(headlessEditor, response.email),
                  attachments
                )
              : undefined;

            const emailLexicalWithOriginal = converter.merge(
              translatedEmailLexical,
              originalEmailLexical
            );

            const smsLexical = response.sms
              ? converter.serialized.fromPlainText(headlessEditor, response.sms)
              : undefined;

            return {
              subject: response.subject ?? "",
              email_lexical: emailLexicalWithOriginal,
              sms_lexical: smsLexical,
              language: response.translated_language,
              language_name: getLanguageName(response.translated_language),
            };
          });
        translationProgress.onCompleted();
        translationProgress.onClose();

        const confirmedTranslations = await confirmTranslation([
          originalMessage,
          ...translations,
        ]);

        return confirmedTranslations;
      } catch (error) {
        if (isAborted(controller)) {
          return undefined;
        }
        throw error;
      } finally {
        clearRef();
        setIsTranslating(false);
        translationProgress.onClose();
      }
    },
    [
      getLanguageName,
      getOriginalMessage,
      converter,
      headlessEditor,
      messageContext,
      translatableLanguages,
      confirmTranslation,
      translationProgress,
      createController,
      isAborted,
      clearRef,
      hasRecipientsWithEmail,
      hasRecipientsWithPhone,
    ]
  );

  return {
    isTranslating,
    progressDrawer: (
      <TranslationProgressDrawer
        isOpen={translationProgress.isOpen}
        onClose={cancelTranslation}
        currentStep={translationProgress.currentStep}
      />
    ),
    previewDrawer: (
      <TranslationPreviewDrawer
        isOpen={!!translationPromptProps}
        translations={translationPromptProps ?? []}
        receiversInfosByLanguage={receiversInfosByLanguage}
        hasRecipientsWithEmail={hasRecipientsWithEmail}
        hasRecipientsWithPhone={hasRecipientsWithPhone}
        onConfirm={resolveTranslation}
        onCancel={() => resolveTranslation(null)}
        messageContext={messageContext}
      />
    ),
    translatableLanguages,
    handleTranslate,
  };
}
